import { LOCAL_STORAGE_KEYS, Context as ContextEnum } from './constants';
import { getOAuthCookieNonce } from './cookies';
import type { Context, MakeOAuth2StateOptions, AuthData } from './types';

export const generateNonce = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  const randomValueStrategy =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    typeof window.crypto?.getRandomValues === 'undefined' ? 'math' : 'crypto';
  switch (randomValueStrategy) {
    case 'math': {
      // eslint-disable-next-line no-magic-numbers
      return (
        // eslint-disable-next-line no-magic-numbers
        Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
      );
    }
    case 'crypto': {
      const randomValues = window.crypto.getRandomValues(new Int16Array(1));
      return String(Math.abs(randomValues[0]));
    }
  }
};

export const UNKNOWN_STATE = 'unknown_state';
export const makeOAuthState = ({ context, provider, nonce = '' }: MakeOAuth2StateOptions) => {
  switch (context) {
    case 'pro-registration':
    case 'request-form':
    case 'sign-in':
    case 'client-registration': {
      return `${context}_${provider}_${nonce}`;
    }
    default: {
      return UNKNOWN_STATE;
    }
  }
};

export const parseOAuthState = (state: string) => {
  const decodedState = decodeURIComponent(state);
  const [context = ContextEnum.unknownContext, provider = '', nonce = ''] = decodedState.split('_');
  return { context: context as Context, provider, nonce };
};

export const isAuthData = (obj: unknown): obj is AuthData =>
  obj !== null &&
  typeof obj === 'object' &&
  typeof (obj as AuthData).expires === 'number' &&
  typeof (obj as AuthData).token === 'string' &&
  typeof (obj as AuthData).uid === 'string';

export const isOAuthStateAndCookieValid = (state: string, cookies: string) => {
  const nonceFromCookie = getOAuthCookieNonce(cookies);
  const { nonce: nonceFromState } = parseOAuthState(state);
  return nonceFromCookie === nonceFromState;
};

export const setAuthenticationData = (authData: AuthData) => {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.setItem(LOCAL_STORAGE_KEYS.AUTH_DATA, JSON.stringify(authData));
  localStorage.setItem(LOCAL_STORAGE_KEYS.USER_ID, JSON.stringify(authData.uid));
  localStorage.setItem(LOCAL_STORAGE_KEYS.FAILED_ATTEMPTS, '0');
};

export type SocialRequestMeta = {
  hasVisitedSocialField?: boolean;
  isNewUser?: boolean;
  jobTypeId: string;
  shouldTrackSocialEvents?: boolean;
  socialRequestId: string | null;
};
export const setSocialRequestMetaData = (metadata: SocialRequestMeta) => {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(LOCAL_STORAGE_KEYS.SOCIAL_REQUEST_META, JSON.stringify(metadata));
};
